/**
 * Regras puras dos componentes de tela (US-4601, US-4605) — Nexora Grid (frontend).
 *
 * Criado em: 02/11/2026 · Última modificação: 02/11/2026 · Versão: 1.0.0
 *
 * Sem DOM: testadas em `testes/tela.test.mjs`.
 */

export type Ordem = { coluna: string; crescente: boolean };

/** Próximo índice ao andar com ↑ ↓ (para nas pontas; sem seleção, começa no primeiro). */
export function indiceVizinho(atual: number, total: number, passo: 1 | -1): number {
  if (total <= 0) return -1;
  if (atual < 0) return 0;
  return Math.min(total - 1, Math.max(0, atual + passo));
}

/** Busca sem acento e sem caixa em vários campos. */
export function normalizar(texto: string): string {
  return texto.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

export function casaBusca(busca: string, ...campos: (string | null | undefined)[]): boolean {
  const b = normalizar(busca);
  return !b || b.split(/\s+/).every((parte) => campos.some((c) => normalizar(c ?? "").includes(parte)));
}

/** Ordena por uma chave (texto, número ou data ISO), estável; nulos no fim. */
export function ordenar<T>(linhas: T[], valor: (l: T) => string | number | null | undefined, crescente = true): T[] {
  const sinal = crescente ? 1 : -1;
  return linhas.map((l, i) => ({ l, i, v: valor(l) })).sort((a, b) => {
    if (a.v == null && b.v == null) return a.i - b.i;
    if (a.v == null) return 1;
    if (b.v == null) return -1;
    const c = typeof a.v === "number" && typeof b.v === "number" ? a.v - b.v
      : String(a.v).localeCompare(String(b.v), "pt-BR", { numeric: true, sensitivity: "base" });
    return c === 0 ? a.i - b.i : c * sinal;
  }).map((x) => x.l);
}

/** "há 3 min", "há 2 h", "há 4 dias" a partir de uma data ISO (vazio se nula). */
export function idadeRelativa(iso: string | null | undefined, agora: number = Date.now()): string {
  if (!iso) return "";
  const min = Math.max(0, Math.round((agora - new Date(iso).getTime()) / 60000));
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.round(min / 60);
  if (h < 48) return `há ${h} h`;
  return `há ${Math.round(h / 24)} dias`;
}

/** Pílulas dos filtros ativos (rótulo legível de cada um). */
export function pilulasAtivas(filtros: Record<string, string>, rotulos: Record<string, (v: string) => string>): { chave: string; texto: string }[] {
  return Object.entries(filtros).filter(([, v]) => v).map(([chave, v]) => ({ chave, texto: rotulos[chave] ? rotulos[chave](v) : v }));
}
